import { isApiError } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Skeleton, TableSkeletonRows, toast } from "@sagot/ui";
import { useId, useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { ChangeAccessDialog } from "../../deploy-knowledge/knowledge/source-dialogs";
import { useUpdateAudiences } from "../../deploy-knowledge/knowledge/use-knowledge";
import { useAudienceLabel } from "../../deploy-knowledge/shared/use-shared-queries";
import { GUEST_AUDIENCE, SIGNED_IN_AUDIENCE, playgroundLink } from "../shared/audience-presets";
import { useSources } from "../shared/queries";
import type { SourceView } from "../shared/types";
import { AgentKnowledgeRow } from "./agent-knowledge-row";
import { audiencesForPreset, isCapNarrowing, toggledIds, visibilitySummary, type AccessPreset } from "./agent-knowledge-model";
import { PiiConfirmDialog } from "./pii-confirm-dialog";
import { SettingsCard } from "./settings-ui";
import { useAgentSourceIds, useSetAgentSources } from "./use-agent-knowledge";

const K = "build.agentKnowledge";
const FORBIDDEN = 403;
const COLUMNS = 5;

interface AgentKnowledgeTabProps {
  agentId: string;
  /** The agent's saved "Who can chat" audiences: the upper limit of what visitors read. */
  audiences: readonly number[];
  /** Ticking sources is an edit of the agent. */
  canLink: boolean;
}

function KnowledgeSkeleton() {
  return (
    <div className="space-y-6" aria-hidden="true" data-testid="agent-knowledge-skeleton">
      <Skeleton className="h-28 rounded-card" />
      <section className="card overflow-hidden">
        <table className="table">
          <tbody><TableSkeletonRows rows={5} columns={COLUMNS} /></tbody>
        </table>
      </section>
    </div>
  );
}

function Summary({ guests, signedIn, cap }: { guests: number; signedIn: number; cap: readonly number[] }) {
  const { t } = useTranslation();
  const labelOf = useAudienceLabel();
  return (
    <div className="space-y-2">
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold" data-testid="knowledge-summary">
        <span className="inline-flex items-center gap-1.5"><Icon name="globe" className="size-4 text-fg-subtle" />{t(`${K}.guestsCan`, { count: guests })}</span>
        <span aria-hidden="true" className="text-fg-subtle">·</span>
        <span className="inline-flex items-center gap-1.5"><Icon name="users" className="size-4 text-fg-subtle" />{t(`${K}.membersCan`, { count: signedIn })}</span>
      </p>
      <p className="sr-only" role="status" aria-live="polite">{t(`${K}.summaryLive`, { guests, members: signedIn })}</p>
      <p className="text-xs text-fg-subtle">{t(`${K}.summaryNote`)}</p>
      {isCapNarrowing(cap) ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${K}.capHint`, { levels: cap.map(labelOf).join(", ") })}</p> : null}
      {cap.includes(GUEST_AUDIENCE) ? null : <p className="text-xs font-semibold text-accent-ink">{t(`${K}.capNoGuests`, { label: labelOf(GUEST_AUDIENCE) })}</p>}
    </div>
  );
}

function TestButtons({ agentId }: { agentId: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const path = consoleRoutes.playground(summary.slug, agentId);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button asChild variant="secondary" size="sm">
        <AppLink href={playgroundLink(path, GUEST_AUDIENCE)}><Icon name="messages-square" />{t(`${K}.testGuest`)}</AppLink>
      </Button>
      <Button asChild variant="secondary" size="sm">
        <AppLink href={playgroundLink(path, SIGNED_IN_AUDIENCE)}><Icon name="user-check" />{t(`${K}.testSignedIn`)}</AppLink>
      </Button>
    </div>
  );
}

/** Agent Settings > Knowledge: pick the sources this agent uses and who may read each one. */
export function AgentKnowledgeTab({ agentId, audiences: cap, canLink }: AgentKnowledgeTabProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { summary: workspace } = useWorkspaceContext();
  const permissions = usePermissions(agentId);
  const canEditAccess = !permissions.readOnly && permissions.can("knowledge.edit", { agentId });
  const sources = useSources();
  const linked = useAgentSourceIds(agentId);
  const setSources = useSetAgentSources(agentId);
  const update = useUpdateAudiences();
  const lockNoteId = useId();
  const [confirming, setConfirming] = useState<SourceView | null>(null);
  const [custom, setCustom] = useState<SourceView | null>(null);

  if (sources.isPending || linked.isPending) return <KnowledgeSkeleton />;
  if (sources.isError || linked.isError) {
    const error = sources.error ?? linked.error;
    const denied = isApiError(error) && error.status === FORBIDDEN;
    if (denied) return <EmptyState icon="lock" title={t(`${K}.forbidden.title`)} description={t(`${K}.forbidden.body`)} />;
    return (
      <ErrorState
        title={t(`${K}.error.title`)}
        description={t(`${K}.error.body`)}
        retryLabel={t("common.retry")}
        onRetry={() => { void sources.refetch(); void linked.refetch(); }}
      />
    );
  }
  const knowledgeHref = consoleRoutes.knowledge(workspace.slug, agentId);
  if (sources.data.length === 0) {
    return (
      <EmptyState
        icon="book-open"
        title={t(`${K}.empty.title`)}
        description={t(`${K}.empty.body`)}
        action={<Button asChild><AppLink href={knowledgeHref}><Icon name="plus" />{t(`${K}.empty.cta`)}</AppLink></Button>}
      />
    );
  }

  const linkedIds = linked.data;
  const totals = visibilitySummary(sources.data, new Set(linkedIds), cap);
  const saveAudiences = (source: SourceView, audiences: number[]) => {
    update.mutate(
      { sourceId: source.id, audiences },
      {
        onSuccess: () => { toast.success(t(`${K}.accessSaved`, { name: source.title })); setConfirming(null); },
        onError: (error) => { toast.error(message(error)); },
      },
    );
  };
  const pick = (source: SourceView, preset: AccessPreset) => {
    if (preset === "custom") setCustom(source);
    else if (preset === "everyone" && source.pii_found.length > 0) setConfirming(source);
    else saveAudiences(source, audiencesForPreset(preset));
  };
  const toggle = (source: SourceView, on: boolean) => {
    setSources.mutate(toggledIds(linkedIds, source.id, on), {
      onSuccess: () => { toast.success(t(on ? `${K}.usedOn` : `${K}.usedOff`, { name: source.title })); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <>
      <SettingsCard title={t(`${K}.summaryLabel`)} hint={t(`${K}.testHint`)} aside={<TestButtons agentId={agentId} />}>
        <Summary guests={totals.guests} signedIn={totals.signedIn} cap={cap} />
      </SettingsCard>
      <SettingsCard
        title={t(`${K}.listTitle`)}
        hint={t(`${K}.listHint`)}
        aside={<Button asChild variant="ghost" size="sm"><AppLink href={knowledgeHref}><Icon name="book-open" />{t(`${K}.manage`)}</AppLink></Button>}
        flush
      >
        {canEditAccess ? null : <p id={lockNoteId} className="border-b border-line bg-surface-2 px-5 py-3 text-xs text-fg-muted">{t(`${K}.needsEdit`)}</p>}
        {canLink ? null : <p className="border-b border-line bg-surface-2 px-5 py-3 text-xs text-fg-muted">{t(`${K}.needsLink`)}</p>}
        <div className="overflow-x-auto">
          <table className="table">
            <caption className="sr-only">{t(`${K}.caption`)}</caption>
            <thead>
              <tr>
                <th><span className="sr-only">{t(`${K}.cols.use`)}</span></th>
                <th>{t(`${K}.cols.source`)}</th>
                <th>{t(`${K}.cols.status`)}</th>
                <th className="text-right">{t(`${K}.cols.chunks`)}</th>
                <th>{t(`${K}.cols.access`)}</th>
              </tr>
            </thead>
            <tbody>
              {sources.data.map((source) => (
                <AgentKnowledgeRow
                  key={source.id}
                  source={source}
                  linked={linkedIds.includes(source.id)}
                  canLink={canLink}
                  canEditAccess={canEditAccess}
                  busy={setSources.isPending || update.isPending}
                  workspaceSlug={workspace.slug}
                  agentId={agentId}
                  lockNoteId={lockNoteId}
                  onToggle={toggle}
                  onPick={pick}
                />
              ))}
            </tbody>
          </table>
        </div>
      </SettingsCard>
      {confirming ? (
        <PiiConfirmDialog
          name={confirming.title}
          kinds={confirming.pii_found}
          pending={update.isPending}
          onCancel={() => { setConfirming(null); }}
          onConfirm={() => { saveAudiences(confirming, audiencesForPreset("everyone")); }}
        />
      ) : null}
      {custom ? <ChangeAccessDialog source={custom} onClose={() => { setCustom(null); }} /> : null}
    </>
  );
}
